import { AuthStore } from '@program-suite/data-auth';
import { MatRadioModule } from '@angular/material/radio';
import {
  ButtonComponent,
  MissingInfoModalComponent,
  MissingInfoModalData,
  ModalService,
  ModalType,
  RequestSubmittedComponent,
  RequestSubmittedModalData,
  SnackbarComponent,
  ToastType,
  UI_FORM_IMPORTS,
} from './../../../../ui-common/src/index';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { dateNotInPastValidator, toDateOnly, zipCodeStateValidator } from '@program-suite/util-helpers';
import { Location } from '@angular/common';
import {
  evenPercentageSplit,
  LookupStore,
  LookupViewModel,
  PolicyChangeStatusEnum,
  ProgramEnum,
  WizardClassViewModel,
  WizardStore,
} from '@program-suite/data-common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CoverageAdjustmentStep1Component } from './coverage-adjustment-step1/coverage-adjustment-step1.component';
import { CoverageAdjustmentStep2Component } from './coverage-adjustment-step2/coverage-adjustment-step2.component';

type FormControls<T> = {
  [K in keyof T]: K extends 'classes'
    ? FormArray<FormGroup<FormControls<WizardClassViewModel>>>
    : FormControl<T[K] | null>;
};

@Component({
  selector: 'lib-coverage-adjustment-form',
  templateUrl: './coverage-adjustment-form.component.html',
  styleUrl: './coverage-adjustment-form.component.scss',
  imports: [
    ...UI_FORM_IMPORTS,
    MatStepperModule,
    ReactiveFormsModule,
    ButtonComponent,
    MatRadioModule,
    MatFormFieldModule,
    MatCheckboxModule,
    CoverageAdjustmentStep1Component,
    CoverageAdjustmentStep2Component
],
  providers: [WizardStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageAdjustmentFormComponent implements OnInit, OnDestroy {
  private _fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  wizardStore = inject(WizardStore);
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  modalService = inject(ModalService);
  private injector = inject(Injector);
  private _stepper = viewChild<MatStepper>('stepper');
  step2 = viewChild<MatStep>('step2');
  destroy$ = new Subject<void>();
  private baseUrl = '';
  reviewing = signal(false);
  // Only what the missing info modal shows; older policies have no type-of-work percentages (total 0),
  // so checking those here would leave the modal's button disabled with nothing to fix
  missingInfoDisabled = computed(() => {
    return (
      this.wizardStore.totalPayroll() < 20000 ||
      this.wizardStore.addAppropriateClassCodeError() ||
      this.wizardStore.totalClassPercentage() !== 100 ||
      this.wizardStore.duplicateClasses() ||
      this.wizardStore.employeePayrollMissing()
    );
  });

  step1Form!: FormGroup;
  step2Form!: FormGroup;

  selectedIndex = signal<number>(0);
  stateDateLookupsInitialized = true;

  data: any;

  get classesFormArray(): FormArray {
    return this.step1Form?.get('classes') as FormArray;
  }

  displayWholesalerButtons = false;
  displayAdminButtons = false;
  backToText = signal<string>('Back to Policy');

  constructor(private location: Location, private snackBar: MatSnackBar) {
    this.data = this.router.currentNavigation()?.extras.state || {};
    var url = this.router.url;
    if (url.includes('pending-request')) {
      this.backToText.set('Back to Pending Request');
    }
    effect(() => {
      if (this.wizardStore.performsNewResConstructionDisabled()) {
        this.step1Form?.get('performsNewResConstruction')?.disable();
      } else {
        this.step1Form?.get('performsNewResConstruction')?.enable();
      }
    });
    effect(() => {
      if (this.wizardStore.patchWizard()) {
        this.patchFormValues();
      }
    });

    if (
      (this.data.policyChangeStatus === PolicyChangeStatusEnum.Draft ||
        this.data.policyChangeStatus === PolicyChangeStatusEnum.PendingSignature ||
        !this.data.policyChangeGuid) &&
      !this.authStore.isAdmin()
    ) {
      this.displayWholesalerButtons = true;
    }
    if (
      ((this.data.policyChangeStatus != PolicyChangeStatusEnum.Issued &&
        this.data.policyChangeStatus != PolicyChangeStatusEnum.Declined &&
        this.data.policyChangeStatus != PolicyChangeStatusEnum.Voided) ||
        !this.data.policyChangeGuid) &&
      this.authStore.isAdmin()
    ) {
      this.displayAdminButtons = true;
    }
  }

  goBack() {
    const url = this.router.url;
    const oldUrl = url.split('/coverage-adjustment/')[0];
    this.router.navigate([oldUrl]);
  }

  async ngOnInit() {
    if (!this.data?.policyGuid) {
      // HACK if the page refreshes we lose are info so we just go back to the policy
      this.goBack();
      return;
    }

    const id = this.data.policyChangeGuid ?? this.data.policyGuid;
    if (this.data.policyChangeGuid) {
      this.wizardStore.setPolicyChangeGuid(this.data.policyChangeGuid);
    }
    if (this.data.policyGuid) {
      this.wizardStore.setPolicyGuid(this.data.policyGuid);
    }
    this.initializeForms();

    await this.wizardStore.loadWizard(id, true);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.wizardStore.reset();
  }

  async proceed(step: number, options?: { save?: boolean }): Promise<void> {
    const formToCheck = this.getStepForm(step);
    // Clear any prior rate-failure marker so this retry isn't blocked by it (set in the catch below).
    if (formToCheck.hasError('rateFailed')) {
      formToCheck.setErrors(null);
    }
    formToCheck.markAllAsTouched();
    if (formToCheck.valid) {
      // Rating (rateWizard handles both save and rate operations)
      if (this.displayAdminButtons || this.displayWholesalerButtons) {
        this.wizardStore.loadSpinner(true);
        try {
          await this.wizardStore.rateWizard();
        } catch (error: any) {
          this.wizardStore.loadSpinner(false);
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: {
              title: 'Rating Failed',
              toastType: ToastType.Error,
              message: error?.error ?? error,
            },
          });
          // Mark the step invalid so neither the button nor the stepper header can advance to Price
          // on a failed rate.
          formToCheck.setErrors({ rateFailed: true });
          return;
        }

        this.wizardStore.loadSpinner(false);
      }
      this._stepper()?.next();
    }
  }

  async review() {
    if (this.step1Form.valid && (this.displayAdminButtons || this.displayWholesalerButtons)) {
      await this.wizardStore.saveWizard();
      this.reviewing.set(true);
    }
  }

  // adding form controls dynamically when patching
  addClass(classData: WizardClassViewModel = { classCode: '', payroll: undefined }): void {
    const classGroup = this._fb.group({
      classCode: [classData.classCode || '', Validators.required],
      // payroll is no longer entered; it's calculated from percentageOfWork
      payroll: [classData.payroll || null],
      percentageOfWork: [classData.percentageOfWork ?? null, [Validators.required, Validators.min(1), Validators.max(100)]],
    });
    this.classesFormArray.push(classGroup);
  }

  // Initialize FormArray with store data
  private syncClassesWithStore(): void {
    const classes = this.wizardStore.getState().classes || [];
    this.classesFormArray.clear(); // Clear existing controls in the FormArray

    // If there are no classes, add one default empty class
    if (!classes.length) {
      this.addClass({ classCode: '', payroll: undefined });
    } else {
      // Add a form group for each class in the store
      classes.forEach((classData) => {
        this.addClass(classData);
      });

      // Saved percentages that aren't the even split were entered by the producer,
      // so mark them edited and step 1 won't auto-populate over them
      const savedPercents = classes.filter((c) => c.classCode).map((c) => c.percentageOfWork);
      const evenSplit = evenPercentageSplit(savedPercents.length);
      if (savedPercents.some((p, i) => p != null && Number(p) !== evenSplit[i])) {
        this.classesFormArray.controls.forEach((row) => row.get('percentageOfWork')?.markAsDirty());
      }
    }
  }

  // Employee count comes from a 0-20 dropdown. Uses the 'message' error so lib-form-dropdown shows it,
  // e.g. for older quotes saved with more than 20 employees when this was a free-text field.
  private numberOfEmployeesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null || control.value === '') {
        return null;
      }
      const employees = Number(control.value);
      return Number.isInteger(employees) && employees >= 0 && employees <= 20
        ? null
        : { message: 'Number of Employees must be between 0 and 20.' };
    };
  }

  // Employee payroll can only be $0 when there are no employees. Uses the 'message' error so
  // lib-form-money shows it in the field's own error space instead of pushing the row down.
  private employeePayrollValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const employees = Number(control.parent?.get('numberOfEmployees')?.value) || 0;
      const payroll = Number(String(control.value ?? '0').replace(/[^0-9.-]+/g, '')) || 0;
      return employees > 0 && payroll <= 0
        ? { message: 'Employee Payroll must be greater than $0 when the applicant has employees.' }
        : null;
    };
  }

  private getStepForm(step: number): FormGroup {
    switch (step) {
      case 1:
        return this.step1Form;
      case 2:
        return this.step2Form;
      default:
        throw new Error('Invalid step number');
    }
  }

  private initializeForms() {
    // Applicant
    this.step1Form = this._fb.group({
      performsNewResConstruction: this._fb.control(null, Validators.required),
      grossReceipts: this._fb.control(null, Validators.required),
      residentialSubcontractorCosts: this._fb.control(null, Validators.required),
      commercialSubcontratorCosts: this._fb.control(null, Validators.required),
      // Null on policies bound before these fields existed, so the producer has to answer it
      numberOfFieldOwners: this._fb.control(null, Validators.required),
      numberOfEmployees: this._fb.control(0, [Validators.required, this.numberOfEmployeesValidator()]),
      employeePayroll: this._fb.control(0, [Validators.required, this.employeePayrollValidator()]),
      classes: this._fb.array<FormGroup<FormControls<WizardClassViewModel>>>([
        this._fb.group<FormControls<WizardClassViewModel>>({
          classCode: this._fb.control(null, Validators.required),
          payroll: this._fb.control(null),
          percentageOfWork: this._fb.control(null, [Validators.required, Validators.min(1), Validators.max(100)]),
        }),
      ]),
      locationAddress1: this._fb.control(null, Validators.required),
      locationAddress2: this._fb.control(null),
      city: this._fb.control(null, Validators.required),
      state: this._fb.control(null, Validators.required),
      zipCode: this._fb.control(null, [Validators.required, zipCodeStateValidator('state')]),
      descriptionOfOperations: this._fb.control(null, Validators.required),
      percentageResidentialNew: this._fb.control(0),
      percentageResidentialRemodel: this._fb.control(0),
      percentageCommercialNew: this._fb.control(0),
      percentageCommercialRemodel: this._fb.control(0),
      percentageIndustrialNew: this._fb.control(0),
      percentageIndustrialRemodel: this._fb.control(0),
    });

    // Review
    this.step2Form = this._fb.group({
      endorsementEffectiveDate: this._fb.control(new Date(), [
        Validators.required,
        dateNotInPastValidator(this.authStore.isAdmin()),
      ]),
    });
  }

  private initializeSubscriptions() {
    // Employee payroll's validity depends on the employee count, so re-check it (and show the error) when the count changes
    this.step1Form.get('numberOfEmployees')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const employeePayroll = this.step1Form.get('employeePayroll');
      employeePayroll?.updateValueAndValidity();
      if (employeePayroll?.invalid) {
        employeePayroll.markAsTouched();
      }
    });
    this.step1Form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.loadDateStateLookups(value.state);
      if (this.wizardStore.classesInitialized()) {
        this.step2()?.stepControl?.setErrors({ invalid: true });
      }
      // Use the form's current value, not the emitted one: step 1 recalculates class payroll
      // during this same change, so the emitted value can hold the old payroll
      this.wizardStore.updateFields(this.step1Form.value);
    });
    this.step2Form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.wizardStore.updateFields(value);
    });
  }

  private loadDateStateLookups(stateCodeFromForm: string) {
    const effectiveDateFromStore = toDateOnly(this.wizardStore?.getState().effectiveDate!);
    const stateCodeFromStore = this.wizardStore?.getState().state!;
    const valueStateCode = stateCodeFromForm;
    //parse value.effectiveDate to YYYY-MM-DD
    if (!this.stateDateLookupsInitialized || stateCodeFromForm !== stateCodeFromStore) {
      this.stateDateLookupsInitialized = true;
      // Load program data only if state or effective date changes
      this.lookupStore.loadProgramDeductibles('1', valueStateCode, effectiveDateFromStore);
      this.lookupStore.loadProgramLimits('1', valueStateCode, effectiveDateFromStore);
      this.lookupStore.loadProgramClasses('1', valueStateCode, effectiveDateFromStore);
      this.wizardStore.getEndorsements(ProgramEnum.RCIP, valueStateCode, effectiveDateFromStore);
      this.lookupStore.loadProgramStates('1', effectiveDateFromStore);
    }
  }

  private patchFormValues() {
    this.wizardStore.setPatchWizard(false);
    // Read before the form is patched: step 1 auto-fills percentages for classes that have none
    const missingStep2Info = this.wizardStore.missingStep2Info();
    this.stateDateLookupsInitialized = false;
    this.loadDateStateLookups(this.wizardStore.getState().state!);
    const wizardStoreState = this.wizardStore.getState();
    this.step1Form.patchValue({
      ...wizardStoreState,
      // Convert to string for dropdown
      agencyId: `${this.wizardStore.agencyId?.()}`,
      producerId: `${this.wizardStore.producerId?.()}`,
      performsNewResConstruction: this.wizardStore.performsNewResConstruction?.(),
      grossReceipts: this.wizardStore.grossReceipts?.(),
      residentialSubcontractorCosts: this.wizardStore.residentialSubcontractorCosts?.(),
      commercialSubcontratorCosts: this.wizardStore.commercialSubcontratorCosts?.(),
      // Policies bound before employee payroll existed have none saved; treat it as $0
      // (the validator still requires more than $0 when there are employees)
      employeePayroll: this.wizardStore.employeePayroll?.() ?? 0,
    });
    this.step2Form.patchValue({
      ...wizardStoreState,
    });
    this.syncClassesWithStore();
    this.wizardStore.setClassesInitialized(true);

    if (!this.displayAdminButtons && !this.displayWholesalerButtons) {
      this.step1Form.disable();
      this.step2Form.disable();
    }
    this.initializeSubscriptions();

    // Only when the producer can make changes; a read-only viewer couldn't get past the modal
    if (missingStep2Info.length && (this.displayAdminButtons || this.displayWholesalerButtons)) {
      this.openMissingInfoModal();
    }
  }

  // Policies bound before the newer rating questions existed need them answered before any changes.
  // The modal fills in step 1's own form; "Proceed to Price" then re-rates for them.
  private openMissingInfoModal(): void {
    const data: MissingInfoModalData = {
      form: this.step1Form,
      disabled: this.missingInfoDisabled,
      message:
        'Great news! The RCIP rater has been updated since this policy was rated. Please provide the following info so we can price this coverage adjustment. Thank you.',
      buttonText: 'Proceed to Price',
      // Same place as the page's back link: the policy, or the pending request in the admin app
      returnText: this.backToText().replace('Back to', 'Return to'),
    };
    this.modalService.open(
      MissingInfoModalComponent,
      {
        modalType: ModalType.Large,
        disableClose: true,
        data,
        // Share this component's WizardStore, which the modal's payroll and percentage totals read
        injector: this.injector,
      },
      async (proceedToPrice: boolean) => {
        // Not ready to answer yet; nothing has been requested, so just go back where they came from
        if (proceedToPrice === false) {
          this.goBack();
          return;
        }
        // What the producer typed in the modal only updated the form, not step 1's own inputs bound to
        // the same controls, so write the values back out to them (no events, nothing recalculates)
        this.step1Form.patchValue(this.step1Form.getRawValue(), { emitEvent: false });
        await this.proceed(1);
      }
    );
  }

  async submitRequest() {
    if (!this.displayAdminButtons && !this.displayWholesalerButtons) {
      return;
    }
    if (this.step2Form.invalid) {
      this.step2Form.markAllAsTouched();
      return;
    }
    try {
      await this.wizardStore.submit();
      this.modalService.open(RequestSubmittedComponent, {
        modalType: ModalType.Medium,
        data: {
          businessName: this.data.businessName,
          policyNumber: this.data.policyNumber,
        } as RequestSubmittedModalData,
      });
      this.goBack();
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: {
          title: 'Submission Failed',
          toastType: 'Error',
          message: error?.error ?? error,
        },
      });
    }
  }

  async issuePolicyChange() {
    if (!this.displayAdminButtons) {
      return;
    }
    try {
      await this.wizardStore.policyChangeIssue();
      this.goBack();
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Success, message: 'Endorsement Issued' },
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Error, message: error?.error ?? error },
      });
    }
  }

  async decline() {
    if (!this.displayAdminButtons) {
      return;
    }
    try {
      await this.wizardStore.policyChangeDecline();
      this.goBack();
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Success, message: 'Endorsement Declined' },
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Error, message: error?.error ?? error },
      });
    }
  }

  async saveAndClose() {
    if (!this.displayAdminButtons && !this.displayWholesalerButtons) {
      return;
    }
    try {
      await this.wizardStore.saveWizard();
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: {
          title: 'Save Failed',
          toastType: ToastType.Error,
          message: error?.error ?? error,
        },
      });
      return;
    }
    this.snackBar.openFromComponent(SnackbarComponent, {
      duration: 5000,
      data: {
        title: 'Coverage Adjustment Saved',
        toastType: 'Success',
      },
    });
    this.goBack();
  }
}
